import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  ArrowTopRightOnSquareIcon,
  Bars3Icon,
  ChevronDownIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { products } from '../data/products';

interface NavChild {
  name: string;
  href: string;
  external?: boolean;
}

interface NavItem {
  name: string;
  href?: string;
  children?: NavChild[];
}

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navigation: NavItem[] = [
    { name: 'Home', href: '/' },
    {
      name: 'Products',
      children: products.map((product) => ({
        name: product.name,
        href: product.url,
        external: true,
      })),
    },
    { name: 'Services', href: '/services' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="container-custom">
        <div className="flex justify-between items-center h-30">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="flex flex-col h-30">
              <img 
                src="/logo1.png" 
                alt="boop logics Logo" 
                className="h-22 w-56"
              />
              {/*<span className="border-2 border-primary-600 relative left-3 italic text-xs text-secondary-500 font-medium mt-1">People first, AI next</span>*/}
            </div>
          </Link>
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigation.map((item) =>
              item.children ? (
                <Menu key={item.name} as="div" className="relative">
                  <MenuButton className="flex items-center gap-1 text-sm font-medium text-secondary-600 hover:text-primary-600 transition-colors duration-200 data-[open]:text-primary-600">
                    {item.name}
                    <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
                  </MenuButton>
                  <MenuItems
                    transition
                    className="absolute left-0 mt-2 w-56 origin-top-left rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none z-50 transition duration-150 ease-out data-[closed]:opacity-0 data-[closed]:scale-95"
                  >
                    {item.children.map((child) => (
                      <MenuItem key={child.name}>
                        <a
                          href={child.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between px-4 py-2 text-sm text-secondary-600 data-[focus]:bg-secondary-50 data-[focus]:text-primary-600"
                        >
                          {child.name}
                          <ArrowTopRightOnSquareIcon
                            className="h-4 w-4 opacity-60"
                            aria-hidden="true"
                          />
                        </a>
                      </MenuItem>
                    ))}
                  </MenuItems>
                </Menu>
              ) : (
                <Link
                  key={item.name}
                  to={item.href!}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    isActive(item.href!)
                      ? 'text-primary-600'
                      : 'text-secondary-600 hover:text-primary-600'
                  }`}
                >
                  {item.name}
                </Link>
              )
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-secondary-600 hover:text-secondary-900"
            >
              {isOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
              {navigation.map((item) =>
                item.children ? (
                  <div key={item.name}>
                    <div className="px-3 py-2 text-base font-medium text-secondary-400">
                      {item.name}
                    </div>
                    {item.children.map((child) => (
                      <a
                        key={child.name}
                        href={child.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between pl-6 pr-3 py-2 text-base font-medium text-secondary-600 hover:text-primary-600 hover:bg-secondary-50 transition-colors duration-200"
                        onClick={() => setIsOpen(false)}
                      >
                        {child.name}
                        <ArrowTopRightOnSquareIcon
                          className="h-4 w-4 opacity-60"
                          aria-hidden="true"
                        />
                      </a>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href!}
                    className={`block px-3 py-2 text-base font-medium transition-colors duration-200 ${
                      isActive(item.href!)
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-secondary-600 hover:text-primary-600 hover:bg-secondary-50'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                )
              )}
              <Link
                to="/contact"
                className="block px-3 py-2 text-base font-medium btn-primary mt-4"
                onClick={() => setIsOpen(false)}
              >
                Get Started
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;

